import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { CaretLeft, CaretRight, CalendarBlank, MapPin, Warning, CalendarPlus } from "@phosphor-icons/react";
import API from "../lib/api";
import { toast } from "sonner";
import { computeRowBars, rowGridTemplateRows, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import ComplianceBadge from "./ComplianceBadge";
import useComplianceBadges from "../hooks/useComplianceBadges";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
// Local-date YYYY-MM-DD formatter — see DispatchBoardPage.js for why we don't use toISOString here.
function fmtISODate(d) {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function sameDay(a, b) { return fmtISODate(a) === fmtISODate(b); }
function startOfWeek(d) { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); } // Monday-start
function startOfMonth(d) { const x = startOfDay(d); x.setDate(1); return x; }
function endOfMonth(d) { const x = startOfMonth(d); x.setMonth(x.getMonth() + 1); return addDays(x, -1); }

// Backend stores `scheduled_start` / `scheduled_end` as `YYYY-MM-DDTHH:MM:SS+00:00`
// — the wall-clock time the user entered, tagged as UTC. If we let `new Date(...)`
// + `toLocaleTimeString()` render it, the browser silently shifts it into local
// TZ (e.g. UK BST = +1h off in summer, or any operative travelling abroad).
// These helpers slice the ISO string directly so what the admin typed is what
// the operative sees — the app is a UK GMT-first product.
function isoDatePart(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return "";
  return iso.slice(0, 10);
}
function isoTimePart(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 16) return "";
  return iso.slice(11, 16);
}

const STATUS_BG = {
  pending: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100",
  scheduled: "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100",
  in_progress: "bg-indigo-100 text-indigo-900 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-100",
  completed: "bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300",
};

function pillClass(status, emergency) {
  if (emergency) return "bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100";
  return STATUS_BG[status] || STATUS_BG.scheduled;
}

async function downloadIcs(path, filename) {
  try {
    const resp = await API.get(path, { responseType: "blob" });
    const blob = new Blob([resp.data], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Calendar file downloaded — open it to add events to your calendar app.");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not download calendar file.");
  }
}

export default function OperativeScheduleCalendar({ apiBasePath = "/in-house-portal", entityRoutePrefix = "/my-work" }) {
  const [view, setView] = useState("week"); // 'day' | 'week' | 'month'
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [items, setItems] = useState([]);
  const [unscheduled, setUnscheduled] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextBeyond, setNextBeyond] = useState(null); // next scheduled item outside current range

  const { rangeStart, rangeEnd, title, isList } = useMemo(() => {
    if (view === "day") {
      return { rangeStart: anchor, rangeEnd: anchor, isList: false, title: anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) };
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      const e = addDays(s, 6);
      return { rangeStart: s, rangeEnd: e, isList: false, title: `${s.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${e.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` };
    }
    if (view === "month") {
      const s = startOfMonth(anchor);
      const e = endOfMonth(anchor);
      return { rangeStart: s, rangeEnd: e, isList: false, title: s.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
    }
    if (view === "next-30") {
      const s = startOfDay(new Date());
      const e = addDays(s, 30);
      return { rangeStart: s, rangeEnd: e, isList: true, title: "Next 30 days" };
    }
    // 'all-assigned' — go back 30 days, forward 365 days; render as list.
    const s = addDays(startOfDay(new Date()), -30);
    const e = addDays(startOfDay(new Date()), 365);
    return { rangeStart: s, rangeEnd: e, isList: true, title: "All assigned" };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`${apiBasePath}/schedule`, {
        params: { start: fmtISODate(rangeStart), end: fmtISODate(rangeEnd) },
      });
      const flat = [
        ...(data.jobs || []).map((j) => ({ ...j, entity_type: "job", emergency: false })),
        ...(data.emergency_jobs || []).map((j) => ({ ...j, entity_type: "emergency_job", emergency: true })),
        ...(data.tasks || []).map((t) => ({ ...t, entity_type: "task", emergency: false })),
      ];
      flat.sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
      setItems(flat);
      setUnscheduled(data.unscheduled_count || 0);

      // If current range shows nothing, look ahead 12 months for the next job so
      // we can tell the user "you have jobs — they're just not in this week".
      if (flat.length === 0) {
        try {
          const future = await API.get(`${apiBasePath}/schedule`, {
            params: {
              start: fmtISODate(addDays(rangeEnd, 1)),
              end: fmtISODate(addDays(rangeEnd, 365)),
            },
          });
          const futureItems = [
            ...(future.data.jobs || []),
            ...(future.data.emergency_jobs || []),
          ].sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
          setNextBeyond(futureItems[0] || null);
        } catch { setNextBeyond(null); }
      } else {
        setNextBeyond(null);
      }
    } catch {
      toast.error("Could not load your schedule");
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, apiBasePath]);

  // Phase 240 — fetch this user's own unavailability so the calendar can
  // visually grey-out days they're off
  const [unavailable, setUnavailable] = useState([]);
  useEffect(() => {
    let mounted = true;
    API.get("/users/me/unavailability").then(({ data }) => {
      if (mounted) setUnavailable(data?.unavailability || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  const isUnavailableISO = useCallback((iso) => {
    return unavailable.find((u) => {
      const s = u.start_date;
      const e = u.end_date || u.start_date;
      return s && e && iso >= s && iso <= e;
    });
  }, [unavailable]);

  useEffect(() => { load(); }, [load]);

  // Phase 239 — drag-and-drop reschedule
  // Permission: in-house ops can reschedule tasks they're on;
  //             admins/PMs can reschedule anything from any portal.
  const handleDrop = useCallback(async (targetIsoDate, dragData) => {
    if (!dragData?.id) return;
    const { id, entity_type, original_iso } = dragData;
    // Compute new start preserving HH:MM (in the same GMT wall-clock the user
    // originally entered — slice directly rather than round-tripping through
    // `Date`, which would apply the browser's local-TZ offset).
    let newStartTime = isoTimePart(original_iso) || "09:00";
    try {
      if (entity_type === "task") {
        await API.put(`/tasks/${id}`, { start_date: targetIsoDate, start_time: newStartTime });
      } else if (entity_type === "emergency_job") {
        await API.put(`/emergency-jobs/${id}`, { schedule: { start_date: targetIsoDate, start_time: newStartTime } });
      } else {
        await API.put(`/jobs/${id}`, { schedule: { start_date: targetIsoDate, start_time: newStartTime } });
      }
      try { (await import("../lib/haptics")).haptic("success"); } catch { /* ignore */ }
      toast.success(`Moved to ${new Date(targetIsoDate + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move — you may not have permission");
    }
  }, [load]);

  const allowDrop = (e) => {
    if (e.dataTransfer.types.includes("application/x-necos-event")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }
  };
  const onDayDrop = (iso) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    let payload = null;
    try { payload = JSON.parse(e.dataTransfer.getData("application/x-necos-event") || "null"); }
    catch { /* ignore */ }
    if (payload) handleDrop(iso, payload);
  };

  const byDay = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const d = isoDatePart(it.scheduled_start);
      if (!d) continue;
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(it);
    }
    return m;
  }, [items]);

  // Compliance ack summary for the visible "job" items only — emergencies
  // and tasks aren't tracked in the compliance hub. Powers the shield badge
  // on every chip and bar so operatives see at a glance which job they
  // STILL need to acknowledge before they can start work.
  const jobIdsInView = useMemo(
    () => Array.from(new Set(items.filter(it => it.entity_type === "job").map(it => it.id))),
    [items],
  );
  const complianceByJob = useComplianceBadges(jobIdsInView);

  // Continuous-bar layout — each item gets normalised startISO/endISO so
  // multi-day jobs span their full date range as ONE bar across week/month
  // views (instead of only showing on the start day).
  const barEvents = useMemo(() => {
    return items.map((it) => {
      const startISO = isoDatePart(it.scheduled_start);
      // Prefer explicit scheduled_end; fall back to nested schedule.end_date
      // (which is YYYY-MM-DD) or finally to the start date itself.
      let endISO = startISO;
      if (it.scheduled_end) endISO = isoDatePart(it.scheduled_end);
      else if (it.schedule?.end_date) endISO = it.schedule.end_date;
      else if (it.end_date) endISO = it.end_date;
      return {
        id: `${it.entity_type || "item"}-${it.id}`,
        startISO,
        endISO: endISO || startISO,
        startTime: isoTimePart(it.scheduled_start),
        item: it,
      };
    }).filter(b => b.startISO);
  }, [items]);

  const step = (dir) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, dir * 7));
    else {
      const x = new Date(anchor); x.setMonth(x.getMonth() + dir); setAnchor(startOfDay(x));
    }
  };

  return (
    <div className="border border-border bg-card rounded-lg" data-testid="operative-schedule-calendar">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CalendarBlank className="h-5 w-5 text-slate-500" />
            <h2 className="text-base font-semibold" data-testid="schedule-title">My schedule — {title}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
              {["day", "week", "month"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`schedule-view-${v}`}
                  className={`text-xs px-2 py-1 rounded capitalize ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                >
                  {v}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => step(-1)} disabled={isList} data-testid="schedule-prev"><CaretLeft size={14} /></Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setAnchor(startOfDay(new Date()))} disabled={isList} data-testid="schedule-today">Today</Button>
            <Button size="sm" variant="ghost" onClick={() => step(1)} disabled={isList} data-testid="schedule-next"><CaretRight size={14} /></Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              disabled={items.length === 0}
              data-testid="schedule-download-ics"
              onClick={() => downloadIcs(
                `${apiBasePath}/schedule.ics?start=${fmtISODate(rangeStart)}&end=${fmtISODate(rangeEnd)}`,
              `necos-schedule-${fmtISODate(rangeStart)}-to-${fmtISODate(rangeEnd)}.ics`,
            )}
          >
            <CalendarPlus size={14} className="mr-1" /> Add to calendar
          </Button>
        </div>
        </div>

        {/* Quick-filter row */}
        <div className="flex items-center gap-1 border border-border rounded-md p-0.5 self-start" data-testid="schedule-quick-filters">
          {[
            { id: "week", label: "This week" },
            { id: "next-30", label: "Next 30 days" },
            { id: "all-assigned", label: "All assigned" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => { setView(f.id); if (f.id === "week") setAnchor(startOfDay(new Date())); }}
              data-testid={`schedule-quick-${f.id}`}
              className={`text-xs px-3 py-1 rounded transition ${view === f.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {unscheduled > 0 && (
        <div className="px-4 py-2 border-b border-border bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2" data-testid="schedule-unscheduled-note">
          <Warning size={14} />
          <span>
            You have <span className="font-semibold">{unscheduled}</span> unscheduled job{unscheduled === 1 ? "" : "s"} without a start date — see the list below.
          </span>
        </div>
      )}

      {items.length === 0 && nextBeyond && (
        <div className="px-4 py-2 border-b border-border bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-800 dark:text-blue-200 flex items-center justify-between gap-2" data-testid="schedule-next-beyond-note">
          <span>
            Nothing in this {view}. Your next job is <span className="font-semibold">{nextBeyond.title}</span> on{" "}
            {new Date(`${isoDatePart(nextBeyond.scheduled_start)}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}.
          </span>
          <button
            type="button"
            className="underline font-medium hover:text-blue-900 dark:hover:text-blue-100 shrink-0"
            onClick={() => setAnchor(startOfDay(new Date(`${isoDatePart(nextBeyond.scheduled_start)}T00:00:00`)))}
            data-testid="jump-to-next-job"
          >
            Jump to it
          </button>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-400">Loading schedule…</div>
      ) : isList ? (
        <ListView items={items} complianceByJob={complianceByJob} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} />
      ) : view === "day" ? (
        <DayView date={anchor} items={byDay.get(fmtISODate(anchor)) || []} complianceByJob={complianceByJob} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} />
      ) : view === "week" ? (
        <WeekView start={rangeStart} barEvents={barEvents} byDay={byDay} complianceByJob={complianceByJob} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} allowDrop={allowDrop} onDayDrop={onDayDrop} isUnavailableISO={isUnavailableISO} />
      ) : (
        <MonthView anchor={anchor} barEvents={barEvents} byDay={byDay} complianceByJob={complianceByJob} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} allowDrop={allowDrop} onDayDrop={onDayDrop} isUnavailableISO={isUnavailableISO} />
      )}
    </div>
  );
}

function JobChip({ item, compact, complianceSummary, apiBasePath = "/in-house-portal", entityRoutePrefix = "/my-work", canEdit = true, onMoved }) {
  const isTask = item.entity_type === "task";
  const href = isTask
    ? `${entityRoutePrefix}/tasks/${item.id}`
    : `${entityRoutePrefix}/${item.entity_type === "emergency_job" ? "emergency" : "jobs"}/${item.id}`;
  const time = isoTimePart(item.scheduled_start);
  const addr = [item.street_address, item.town_city].filter(Boolean).join(", ") || item.property_name || "";
  const onIcsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    downloadIcs(
      `${apiBasePath}/${item.entity_type}/${item.id}/ics`,
      `necos-${item.entity_type}-${item.id}.ics`,
    );
  };
  const onDragStart = (e) => {
    if (!canEdit) return;
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-necos-event", JSON.stringify({
        id: item.id, entity_type: item.entity_type, original_iso: item.scheduled_start || "",
      }));
    } catch { /* ignore */ }
  };
  return (
    <Link
      to={href}
      draggable={canEdit}
      onDragStart={onDragStart}
      className={`block border rounded px-2 py-1 text-left ${pillClass(item.status, item.emergency)} hover:ring-1 hover:ring-primary/50 transition relative group ${canEdit ? "cursor-move" : ""} ${isTask ? "border-dashed" : ""}`}
      data-testid={`schedule-item-${item.id}`}
      data-entity={item.entity_type}
    >
      {compact ? (
        <div className="truncate text-[11px] pr-4">
          {item.emergency && <span className="font-bold mr-1">!</span>}
          {isTask && <span className="font-bold mr-0.5">·</span>}
          {time && <span className="font-semibold">{time} </span>}
          <span>{item.title}</span>
          {complianceSummary && <span className="ml-1 inline-flex align-middle"><ComplianceBadge summary={complianceSummary} jobId={item.id} size={10} /></span>}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5 pr-5">
          <div className="flex items-center gap-1.5 text-xs font-semibold">
            {item.emergency && <span className="uppercase tracking-wide text-[9px] bg-red-600 text-white px-1 py-0.5 rounded">SOS</span>}
            {isTask && <span className="uppercase tracking-wide text-[9px] bg-violet-600 text-white px-1 py-0.5 rounded">TASK</span>}
            {time && <span>{time}</span>}
            <span className="truncate">{item.title}</span>
            <ComplianceBadge summary={complianceSummary} jobId={item.id} size={11} />
          </div>
          {addr && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground dark:text-slate-300/70">
              <MapPin size={10} /> <span className="truncate">{addr}</span>
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onIcsClick}
        title="Add this job to your calendar"
        aria-label="Add to calendar"
        data-testid={`schedule-item-ics-${item.id}`}
        className="absolute top-1 right-1 p-0.5 rounded opacity-60 hover:opacity-100 hover:bg-white/40 dark:hover:bg-white/10 transition"
      >
        <CalendarPlus size={compact ? 10 : 12} />
      </button>
    </Link>
  );
}

function DayView({ date, items, complianceByJob, apiBasePath, entityRoutePrefix }) {
  return (
    <div className="p-4" data-testid="schedule-day-view">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">Nothing scheduled for {date.toLocaleDateString()}.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => <JobChip key={it.id} item={it} complianceSummary={it.entity_type === "job" ? complianceByJob[it.id] : null} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} />)}
        </div>
      )}
    </div>
  );
}

function ListView({ items, complianceByJob, apiBasePath, entityRoutePrefix }) {
  // Group chronologically by date
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const iso = isoDatePart(it.scheduled_start);
      if (!iso) continue;
      if (!m.has(iso)) m.set(iso, []);
      m.get(iso).push(it);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (groups.length === 0) {
    return <p className="p-8 text-sm text-slate-500 text-center" data-testid="schedule-list-empty">Nothing in this range.</p>;
  }
  return (
    <div className="p-4 space-y-4" data-testid="schedule-list-view">
      {groups.map(([dateIso, list]) => {
        const d = new Date(`${dateIso}T00:00:00`);
        const isToday = sameDay(d, new Date());
        return (
          <div key={dateIso}>
            <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1.5 ${isToday ? "text-primary" : "text-muted-foreground"}`}>
              {isToday ? "Today · " : ""}{d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
            </div>
            <div className="space-y-1.5">
              {list.map((it) => <JobChip key={it.id} item={it} complianceSummary={it.entity_type === "job" ? complianceByJob[it.id] : null} apiBasePath={apiBasePath} entityRoutePrefix={entityRoutePrefix} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekView({ start, barEvents, complianceByJob, apiBasePath, entityRoutePrefix, allowDrop, onDayDrop, isUnavailableISO }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const rowDays = days.map(d => fmtISODate(d));
  const { bars, maxSlot } = computeRowBars(rowDays, barEvents);
  const MAX_VISIBLE = 12;
  const visible = bars.filter(b => b.slot < MAX_VISIBLE);
  const hidden = new Map(rowDays.map(d => [d, 0]));
  bars.forEach(b => {
    if (b.slot < MAX_VISIBLE) return;
    for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
      hidden.set(rowDays[c], (hidden.get(rowDays[c]) || 0) + 1);
    }
  });
  const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));
  return (
    <div
      className="grid grid-cols-7 divide-x divide-border relative"
      style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid) }}
      data-testid="schedule-week-view"
    >
      {days.map((d, i) => {
        const iso = fmtISODate(d);
        const isToday = sameDay(d, new Date());
        const ua = isUnavailableISO?.(iso);
        const hiddenCount = hidden.get(iso) || 0;
        return (
          <div
            key={iso}
            style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
            className={`min-h-[200px] p-2 flex flex-col gap-1 transition-colors hover:bg-muted/30 relative ${ua ? "bg-amber-50 dark:bg-amber-950/20" : ""}`}
            data-testid={`schedule-week-day-${iso}`}
            onDragOver={allowDrop}
            onDrop={onDayDrop(iso)}
          >
            <div className={`text-[10px] uppercase tracking-wide mb-1 font-semibold ${isToday ? "text-primary" : "text-muted-foreground"}`}>
              {WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}
            </div>
            {ua && (
              <div className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold uppercase mb-1" data-testid={`schedule-unavailable-${iso}`}>
                {ua.reason || "off"}{ua.note ? ` · ${ua.note}` : ""}
              </div>
            )}
            {hiddenCount > 0 && (
              <p className="absolute bottom-1 left-2 text-[10px] text-muted-foreground font-medium pointer-events-none">+{hiddenCount} more</p>
            )}
          </div>
        );
      })}
      {visible.map(b => (
        <BarChip
          key={b.event.id}
          item={b.event.item}
          bar={b}
          complianceSummary={b.event.item?.entity_type === "job" ? complianceByJob[b.event.item.id] : null}
          apiBasePath={apiBasePath}
          entityRoutePrefix={entityRoutePrefix}
        />
      ))}
    </div>
  );
}

function MonthView({ anchor, barEvents, complianceByJob, apiBasePath, entityRoutePrefix, allowDrop, onDayDrop, isUnavailableISO }) {
  const firstOfMonth = startOfMonth(anchor);
  const gridStart = startOfWeek(firstOfMonth);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)); // 6 rows x 7
  const currentMonth = anchor.getMonth();
  return (
    <div data-testid="schedule-month-view">
      <div className="grid grid-cols-7 border-b border-border">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold border-r border-border last:border-r-0">{w}</div>
        ))}
      </div>
      {Array.from({ length: 6 }).map((_, rowIdx) => {
        const rowCells = cells.slice(rowIdx * 7, rowIdx * 7 + 7);
        const rowDays = rowCells.map(d => fmtISODate(d));
        const { bars, maxSlot } = computeRowBars(rowDays, barEvents);
        const MAX_VISIBLE = 4;
        const visible = bars.filter(b => b.slot < MAX_VISIBLE);
        const hidden = new Map(rowDays.map(d => [d, 0]));
        bars.forEach(b => {
          if (b.slot < MAX_VISIBLE) return;
          for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
            hidden.set(rowDays[c], (hidden.get(rowDays[c]) || 0) + 1);
          }
        });
        const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));
        return (
          <div
            key={rowIdx}
            className="grid grid-cols-7 relative"
            style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid) }}
          >
            {rowCells.map((d, i) => {
              const iso = fmtISODate(d);
              const isToday = sameDay(d, new Date());
              const inMonth = d.getMonth() === currentMonth;
              const ua = isUnavailableISO?.(iso);
              const hiddenCount = hidden.get(iso) || 0;
              return (
                <div
                  key={iso}
                  style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
                  className={`min-h-[100px] p-1.5 border-r border-border last:border-r-0 transition-colors hover:bg-muted/30 relative ${rowIdx < 5 ? "border-b" : ""} ${inMonth ? (ua ? "bg-amber-50 dark:bg-amber-950/20" : "bg-card") : "bg-muted/40 text-muted-foreground/60"}`}
                  data-testid={`schedule-month-cell-${iso}`}
                  onDragOver={allowDrop}
                  onDrop={onDayDrop(iso)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[11px] font-semibold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</span>
                    {ua && <span className="text-[8px] uppercase font-bold bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-200 px-1 rounded" data-testid={`schedule-unavailable-${iso}`}>{ua.reason || "off"}</span>}
                  </div>
                  {hiddenCount > 0 && (
                    <p className="absolute bottom-1 left-1.5 text-[10px] text-muted-foreground font-medium pointer-events-none">+{hiddenCount} more</p>
                  )}
                </div>
              );
            })}
            {visible.map(b => (
              <BarChip
                key={b.event.id}
                item={b.event.item}
                bar={b}
                compact
                complianceSummary={b.event.item?.entity_type === "job" ? complianceByJob[b.event.item.id] : null}
                apiBasePath={apiBasePath}
                entityRoutePrefix={entityRoutePrefix}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Continuous-bar chip — rendered inside a CSS grid row that has 7 columns.
 * Uses `gridColumn`/`gridRow` style to span across the days the job covers.
 */
function BarChip({ item, bar, compact, complianceSummary, apiBasePath = "/in-house-portal", entityRoutePrefix = "/my-work" }) {
  const isTask = item.entity_type === "task";
  const href = isTask
    ? `${entityRoutePrefix}/tasks/${item.id}`
    : `${entityRoutePrefix}/${item.entity_type === "emergency_job" ? "emergency" : "jobs"}/${item.id}`;
  const time = isoTimePart(item.scheduled_start);
  const onDragStart = (e) => {
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-necos-event", JSON.stringify({
        id: item.id, entity_type: item.entity_type, original_iso: item.scheduled_start || "",
      }));
    } catch { /* ignore */ }
  };
  return (
    <Link
      to={href}
      draggable
      onDragStart={onDragStart}
      className={`flex items-center gap-1 px-1.5 rounded border ${pillClass(item.status, item.emergency)} ${isTask ? "border-dashed" : ""} cursor-move hover:ring-1 hover:ring-primary/50 transition`}
      style={{
        gridColumn: `${bar.startCol} / span ${bar.span}`,
        gridRow: 2 + bar.slot,
        height: `${BAR_HEIGHT_PX - 2}px`,
        marginLeft: bar.continuesLeft ? 0 : 3,
        marginRight: bar.continuesRight ? 0 : 3,
        marginTop: 1,
        alignSelf: "start",
        position: "relative",
        zIndex: 5,
      }}
      data-testid={`schedule-item-${item.id}${bar.isMultiDay ? "-bar" : ""}`}
      data-entity={item.entity_type}
      title={`${item.title}${bar.isMultiDay ? ` (${bar.clampedStartISO} → ${bar.clampedEndISO})` : ""}`}
    >
      {item.emergency && <span className="text-[9px] font-bold shrink-0">!</span>}
      {isTask && <span className="text-[9px] font-bold shrink-0">·</span>}
      {bar.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
      {time && !compact && <span className="text-[10px] font-semibold shrink-0">{time}</span>}
      <span className={`truncate flex-1 ${compact ? "text-[11px]" : "text-[11px] font-semibold"}`}>{item.title}</span>
      <ComplianceBadge summary={complianceSummary} jobId={item.id} size={10} />
      {bar.continuesRight && <span className="opacity-60 shrink-0">›</span>}
    </Link>
  );
}
